import React, { useEffect, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Alert } from '@mui/material';
import { useAppSelector } from '../store/hooks.ts';
import { selectCurrentOrganization } from '../store/organization/organizationSlice.ts';
import { getAvailableModules, MODULE_STORAGE_KEY } from '../modules/moduleRegistry.tsx';
import { useNavigate } from 'react-router-dom';

interface ModuleSwitcherProps {
  activeModuleId?: string | null;
  onModuleChange?: (moduleId: string) => void;
}

export const ModuleSwitcher: React.FC<ModuleSwitcherProps> = ({ activeModuleId, onModuleChange }) => {
  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const [selectedModule, setSelectedModule] = useState<string | ''>('');
  const navigate = useNavigate();

  const availableModules = React.useMemo(
    () => getAvailableModules(currentOrganization),
    [currentOrganization],
  );

  useEffect(() => {
    if (!currentOrganization) {
      return;
    }

    // Initialize selected module from localStorage if present and allowed
    const stored = localStorage.getItem(MODULE_STORAGE_KEY);
    if (stored && availableModules.some((m) => m.id === stored)) {
      setSelectedModule(stored);
      return;
    }

    // Otherwise select first available module (if any)
    if (availableModules.length > 0) {
      setSelectedModule(availableModules[0].id);
      localStorage.setItem(MODULE_STORAGE_KEY, availableModules[0].id);
    } else {
      setSelectedModule('');
      localStorage.removeItem(MODULE_STORAGE_KEY);
    }
  }, [availableModules, currentOrganization]);

  useEffect(() => {
    if (activeModuleId && activeModuleId !== selectedModule) {
      setSelectedModule(activeModuleId);
    }
  }, [activeModuleId, selectedModule]);

  const handleModuleChange = (event: SelectChangeEvent<string>) => {
    const moduleId = event.target.value as string;
    setSelectedModule(moduleId);
    localStorage.setItem(MODULE_STORAGE_KEY, moduleId);
    onModuleChange?.(moduleId);

    const targetModule = availableModules.find((m) => m.id === moduleId);
    const targetPath = targetModule?.navItems?.[0]?.path ?? '/';
    navigate(targetPath, { replace: true });
  };

  if (!currentOrganization) {
    // Don't show module switcher when no organization is selected
    return null;
  }

  if (availableModules.length === 0) {
    return (
      <Box sx={{ minWidth: 200, mr: 2 }}>
        <Alert severity="info">No modules are enabled for this organization.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 200, mr: 2 }}>
      <FormControl fullWidth size="small">
        <InputLabel id="module-select-label" sx={{ color: 'white', '&.Mui-focused': { color: 'white' } }}>
          Module
        </InputLabel>
        <Select
          labelId="module-select-label"
          id="module-select"
          value={selectedModule || ''}
          label="Module"
          onChange={handleModuleChange}
          sx={{
            color: 'white',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.7)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
            },
            '.MuiSvgIcon-root': {
              color: 'white',
            },
          }}
        >
          {availableModules.map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.name} {selectedModule === m.id && ' (Active)'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ModuleSwitcher;
